import { defineStore } from 'pinia';

export interface EnvsStateInterface {
  API_BASE: string | undefined;
  OIDC_AUTH_DOMAIN: string | undefined;
  OIDC_CLIENT_ID: string | undefined;
  OIDC_CLIENT_SCOPE: string | undefined;
  SENTRY_DSN: string | undefined;
  SENTRY_ENVIRONMENT: string | undefined;
  SENTRY_TUNNEL_URL: string | undefined;
  SENTRY_DEBUG: string | undefined;
  WS_BASE: string | undefined;
  DASHBOARD_UPCOMING_APPOINTMENT_LIMIT: string | undefined;
  SCRIBE_STAFF_GUIDE_URL: string | undefined;
  WAGTAIL_API_BASE: string | undefined;
}

type EnvKey = keyof EnvsStateInterface;

// Values Quasar inlined from .env at build time. Vite only replaces static
// `import.meta.env.KEY` reads, so every key is listed explicitly.
const buildEnv: Partial<EnvsStateInterface> = {
  API_BASE: import.meta.env.API_BASE,
  OIDC_AUTH_DOMAIN: import.meta.env.OIDC_AUTH_DOMAIN,
  OIDC_CLIENT_ID: import.meta.env.OIDC_CLIENT_ID,
  OIDC_CLIENT_SCOPE: import.meta.env.OIDC_CLIENT_SCOPE,
  SENTRY_DSN: import.meta.env.SENTRY_DSN,
  SENTRY_ENVIRONMENT: import.meta.env.SENTRY_ENVIRONMENT,
  SENTRY_TUNNEL_URL: import.meta.env.SENTRY_TUNNEL_URL,
  SENTRY_DEBUG: import.meta.env.SENTRY_DEBUG,
  WS_BASE: import.meta.env.WS_BASE,
  DASHBOARD_UPCOMING_APPOINTMENT_LIMIT: import.meta.env
    .DASHBOARD_UPCOMING_APPOINTMENT_LIMIT,
  SCRIBE_STAFF_GUIDE_URL: import.meta.env.SCRIBE_STAFF_GUIDE_URL,
  WAGTAIL_API_BASE: import.meta.env.WAGTAIL_API_BASE,
};

// On the SSR server the live container environment wins over build-time values.
// In the browser only the build-time values exist (config.js is handled by the caller).
function fallbackEnv(key: EnvKey): string | undefined {
  if (import.meta.env.QUASAR_SERVER) {
    return process.env[key] || buildEnv[key];
  }
  return buildEnv[key];
}

export const useEnvsStore = defineStore('envs', {
  state: (): EnvsStateInterface => ({
    API_BASE: '',
    OIDC_AUTH_DOMAIN: '',
    OIDC_CLIENT_ID: '',
    OIDC_CLIENT_SCOPE: '',
    SENTRY_DSN: '',
    SENTRY_ENVIRONMENT: '',
    SENTRY_TUNNEL_URL: '',
    SENTRY_DEBUG: '',
    WS_BASE: '',
    DASHBOARD_UPCOMING_APPOINTMENT_LIMIT: '',
    SCRIBE_STAFF_GUIDE_URL: '',
    WAGTAIL_API_BASE: '',
  }),

  actions: {
    loadEnvs(): void {
      let envConfig: Partial<EnvsStateInterface> = {};

      if (typeof config !== 'undefined') {
        envConfig = config;
      }

      this.API_BASE = envConfig.API_BASE || fallbackEnv('API_BASE');
      this.OIDC_AUTH_DOMAIN =
        envConfig.OIDC_AUTH_DOMAIN || fallbackEnv('OIDC_AUTH_DOMAIN');
      this.OIDC_CLIENT_ID =
        envConfig.OIDC_CLIENT_ID || fallbackEnv('OIDC_CLIENT_ID');
      this.OIDC_CLIENT_SCOPE =
        envConfig.OIDC_CLIENT_SCOPE || fallbackEnv('OIDC_CLIENT_SCOPE');
      this.SENTRY_DSN = envConfig.SENTRY_DSN || fallbackEnv('SENTRY_DSN');
      this.SENTRY_ENVIRONMENT =
        envConfig.SENTRY_ENVIRONMENT || fallbackEnv('SENTRY_ENVIRONMENT');
      this.SENTRY_TUNNEL_URL =
        envConfig.SENTRY_TUNNEL_URL || fallbackEnv('SENTRY_TUNNEL_URL');
      this.SENTRY_DEBUG = envConfig.SENTRY_DEBUG || fallbackEnv('SENTRY_DEBUG');
      this.WS_BASE = envConfig.WS_BASE || fallbackEnv('WS_BASE');
      this.DASHBOARD_UPCOMING_APPOINTMENT_LIMIT =
        envConfig.DASHBOARD_UPCOMING_APPOINTMENT_LIMIT ||
        fallbackEnv('DASHBOARD_UPCOMING_APPOINTMENT_LIMIT');
      this.SCRIBE_STAFF_GUIDE_URL =
        envConfig.SCRIBE_STAFF_GUIDE_URL ||
        fallbackEnv('SCRIBE_STAFF_GUIDE_URL');
      this.WAGTAIL_API_BASE =
        envConfig.WAGTAIL_API_BASE || fallbackEnv('WAGTAIL_API_BASE');
    },
  },
});
